import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Descriptions, 
  Tag, 
  Button, 
  Space, 
  message,
  Modal,
  Divider,
  Image,
  Table,
  Input,
  Checkbox
} from 'antd';
import { 
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { productGroupService } from '../services/productGroupService';
import { productService } from '../services/productService';
import ProductTable from '../components/ProductTable';
import type { ProductGroup, I18nData, SpecDetail } from '../types/productGroup';
import type { Product } from '../types/product';
import {
  getProductGroupCategoryLabel,
  pickProductGroupCategoryFields,
} from '../constants/productGroupCategory';
import { LANG_LIST, LangBlock, LangKey, TextLine } from '../components/I18nDetailBlocks';
import { useLanguage } from '../contexts/LanguageContext';

const ProductGroupDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [productGroup, setProductGroup] = useState<ProductGroup | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // 添加商品弹窗相关状态
  const [addProductModalVisible, setAddProductModalVisible] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [addProductLoading, setAddProductLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProductGroupDetail(id);
      fetchGroupProducts(id);
    }
  }, [id]);

  // 获取商品组详情
  const fetchProductGroupDetail = async (groupId: string) => {
    try {
      setLoading(true);
      const response = await productGroupService.getProductGroupDetail(groupId);
      const data = response.data;
      
      if (data) {
        setProductGroup(data);
      } else {
        message.error('商品组不存在');
        navigate('/product-group');
      }
    } catch (error) {
      console.error('获取商品组详情失败:', error);
      message.error('获取商品组详情失败');
      navigate('/product-group');
    } finally {
      setLoading(false);
    }
  };

  // 获取商品组关联的商品列表
  const fetchGroupProducts = async (groupId: string, page = 1, size = 20) => {
    setProductsLoading(true);
    try {
      const response = await productGroupService.getGroupProducts(groupId);
      // getGroupProducts 返回的是 List，不是分页数据
      const products = response.data || [];
      setProducts(products);
      setTotal(products.length);
      setCurrent(page);
      setPageSize(size);
    } catch (error) {
      console.error('获取关联商品失败:', error);
      message.error('获取关联商品失败');
    } finally {
      setProductsLoading(false);
    }
  };

  // 处理分页变化
  const handlePageChange = (page: number, size: number) => {
    if (id) {
      fetchGroupProducts(id, page, size);
    }
  };

  // 编辑商品组
  const handleEdit = () => {
    navigate(`/product-group/edit/${id}`);
  };

  // 添加商品到组
  const handleAddProduct = async () => {
    setAddProductModalVisible(true);
    setSelectedProductIds([]);
    
    // 获取可用的商品列表（排除已经在组中的商品）
    try {
      const response = await productService.getProductList({
        page: 1,
        size: 100,
        search: ''
      });
      
      // 过滤掉已经在商品组中的商品
      const currentProductIds = products.map(p => p.id);
      const available = response.data.records.filter((product: Product) => 
        !currentProductIds.includes(product.id)
      );
      setAvailableProducts(available);
    } catch (error) {
      message.error('获取商品列表失败');
    }
  };

  // 确认添加商品到组
  const handleConfirmAddProducts = async () => {
    if (selectedProductIds.length === 0) {
      message.warning('请选择要添加的商品');
      return;
    }

    setAddProductLoading(true);
    try {
      await productGroupService.addProductsToGroup(id!, selectedProductIds);
      message.success(`成功添加 ${selectedProductIds.length} 个商品到商品组`);
      setAddProductModalVisible(false);
      fetchGroupProducts(id!, current, pageSize);
    } catch (error) {
      message.error('添加商品失败');
    } finally {
      setAddProductLoading(false);
    }
  };

  // 从组中移除商品
  const handleRemoveProduct = (productId: string) => {
    Modal.confirm({
      title: '确认移除',
      content: '确定要从商品组中移除这个商品吗？',
      onOk: async () => {
        try {
          await productGroupService.removeProductFromGroup(id!, productId);
          message.success('移除成功');
          fetchGroupProducts(id!, current, pageSize);
        } catch (error) {
          message.error('移除失败');
        }
      }
    });
  };

  // 查看商品详情
  const handleViewProduct = (productId: string) => {
    navigate(`/product/detail/${productId}`);
  };

  // 编辑商品
  const handleEditProduct = (productId: string) => {
    navigate(`/product/edit/${productId}`);
  };


  const getI18nText = (
    lang: LangKey,
    field: keyof Pick<I18nData, 'name' | 'remark' | 'specialTips'>
  ): string => {
    if (!productGroup) return '';
    const fromI18n = productGroup.i18n?.[lang]?.[field];
    if (fromI18n) return fromI18n;
    if (lang === 'zh') {
      return (productGroup[field] as string | undefined) || '';
    }
    return '';
  };

  const getI18nLogoUrl = (lang: LangKey): string | undefined => {
    if (!productGroup) return undefined;
    const fromI18n = productGroup.i18n?.[lang]?.logoUrl;
    if (fromI18n) return fromI18n;
    if (lang === 'zh') return productGroup.logoUrl;
    return undefined;
  };

  const renderSpecs = () => {
    if (!productGroup?.specDetails?.length) return null;

    const groupedSpecs = productGroup.specDetails.reduce<Record<string, SpecDetail[]>>((acc, spec) => {
      const groupName = spec.parentName || spec.name;
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(spec);
      return acc;
    }, {});

    return (
      <Card title={t('productGroupSpecsTitle')} className="content-card" style={{ marginBottom: 16 }}>
        <div
          data-i18n-ignore="true"
          style={{
            background: '#fafafa',
            padding: 16,
            borderRadius: 6,
            border: '1px solid #f0f0f0',
          }}
        >
          {Object.entries(groupedSpecs).map(([groupName, specs]) => (
            <div key={groupName} style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#666',
                  marginBottom: 10,
                  borderBottom: '1px solid #e8e8e8',
                  paddingBottom: 4,
                }}
              >
                {groupName}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {specs.map((spec) => (
                  <div
                    key={spec.id}
                    style={{
                      background: '#fff',
                      border: '1px solid #e8e8e8',
                      borderRadius: 6,
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center' }}>
                      <span>
                        <Tag color="red">中文</Tag>
                        {spec.name}
                      </span>
                      {spec.nameEn ? (
                        <span>
                          <Tag color="blue">EN</Tag>
                          {spec.nameEn}
                        </span>
                      ) : (
                        <span style={{ color: '#bbb', fontSize: 13 }}>
                          <Tag color="blue">EN</Tag>暂无
                        </span>
                      )}
                      {spec.namePl ? (
                        <span>
                          <Tag color="gold">PL</Tag>
                          {spec.namePl}
                        </span>
                      ) : (
                        <span style={{ color: '#bbb', fontSize: 13 }}>
                          <Tag color="gold">PL</Tag>暂无
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  if (loading && !productGroup) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        加载中...
      </div>
    );
  }

  if (!productGroup) {
    return null;
  }

  const categoryFields = pickProductGroupCategoryFields(productGroup);

  return (
    <div>
      <div className="page-header">
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/product-group')}
          >
            返回
          </Button>
          <h1 className="page-title">{t('productGroupDetail')}</h1>
          <Button 
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            编辑
          </Button>
        </Space>
      </div>

      <Card title={t('productGroupInfo')} className="content-card" style={{ marginBottom: 16 }} loading={loading}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('productGroupNameMain')} span={2}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {productGroup.logoUrl && (
                <Image
                  width={40}
                  height={40}
                  src={productGroup.logoUrl}
                  alt={productGroup.name}
                  style={{ borderRadius: 6, objectFit: 'cover', marginRight: 12 }}
                />
              )}
              <div>
                <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{productGroup.name}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>ID: {productGroup.id}</div>
              </div>
            </div>
          </Descriptions.Item>

          <Descriptions.Item label={t('productGroupTypeLabel')}>
            <Tag color="blue">{getProductGroupCategoryLabel(categoryFields.code, categoryFields.desc)}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldSort')}>{productGroup.seq || 0}</Descriptions.Item>

          <Descriptions.Item label={t('fieldStatus')}>
            <Tag color={productGroup.isShow === 1 ? 'green' : 'red'}>
              {productGroup.isShow === 1 ? '已上架' : '已下架'}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldCreateTime')}>{productGroup.gmtCreate || '--'}</Descriptions.Item>

          <Descriptions.Item label={t('fieldUpdateTime')}>{productGroup.gmtModified || '--'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('multiLangContent')} className="content-card" style={{ marginBottom: 16 }}>
        {LANG_LIST.map((lang) => (
          <LangBlock key={lang.key} lang={lang}>
            <TextLine label={t('productGroupName')} value={getI18nText(lang.key, 'name')} />
            <TextLine label={t('fieldSpecialTips')} value={getI18nText(lang.key, 'specialTips')} />
            <TextLine label={t('fieldRemark')} value={getI18nText(lang.key, 'remark')} />
          </LangBlock>
        ))}
      </Card>

      <Card title={t('multiLangLogo')} className="content-card" style={{ marginBottom: 16 }}>
        {LANG_LIST.map((lang) => {
          const logoUrl = getI18nLogoUrl(lang.key);
          return (
            <LangBlock key={lang.key} lang={lang}>
              {logoUrl ? (
                <Image
                  width={64}
                  height={64}
                  src={logoUrl}
                  alt={`LOGO ${lang.label}`}
                  style={{ borderRadius: 6, objectFit: 'cover', border: '1px solid #d9d9d9' }}
                />
              ) : (
                <span style={{ color: '#bbb', fontSize: 13 }}>{t('emptyNone')}</span>
              )}
            </LangBlock>
          );
        })}
      </Card>

      {renderSpecs()}

      <Card 
        className="content-card" 
        title={t('relatedProducts')}
        extra={
          <Button 
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddProduct}
          >
            添加商品
          </Button>
        }
      >
        <ProductTable
          products={products}
          loading={productsLoading}
          total={total}
          current={current}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onRemoveFromGroup={handleRemoveProduct}
        />
      </Card>

      {/* 添加商品弹窗 */}
      <Modal
        title="添加商品到商品组"
        open={addProductModalVisible}
        onOk={handleConfirmAddProducts}
        onCancel={() => setAddProductModalVisible(false)}
        width={800}
        confirmLoading={addProductLoading}
        okText="确认添加"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <span>已选择 {selectedProductIds.length} 个商品</span>
        </div>
        <Table
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedProductIds,
            onChange: (selectedRowKeys: React.Key[]) => {
              setSelectedProductIds(selectedRowKeys as string[]);
            },
          }}
          columns={[
            {
              title: '商品图片',
              dataIndex: 'logoUrl',
              key: 'logoUrl',
              width: 80,
              render: (logoUrl: string) => (
                <Image
                  width={40}
                  height={40}
                  src={logoUrl}
                  alt="商品图片"
                  style={{ borderRadius: 4 }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                />
              ),
            },
            {
              title: '商品名称',
              dataIndex: 'name',
              key: 'name',
              ellipsis: true,
            },
            {
              title: '商品类型',
              dataIndex: 'categoryDesc',
              key: 'categoryDesc',
              render: (categoryDesc: string) => (
                <Tag color="blue">{categoryDesc}</Tag>
              ),
            },
            {
              title: '原价',
              dataIndex: 'price',
              key: 'price',
              render: (price: number) => `¥${price?.toFixed(2) || '0.00'}`,
            },
            {
              title: '状态',
              dataIndex: 'isShow',
              key: 'isShow',
              render: (isShow: number) => (
                <Tag color={isShow === 1 ? 'green' : 'red'}>
                  {isShow === 1 ? '已上架' : '已下架'}
                </Tag>
              ),
            },
          ]}
          dataSource={availableProducts}
          rowKey="id"
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Modal>
    </div>
  );
};

export default ProductGroupDetail;
